import { getAuthenticatedClient } from "../supabaseClient";

// Important: this list needs to exactly match the columns in the fleet data table
export const fleetDataList = [
  "equipment_id",
  "Equipment ID",
  "user_id",
  "description",
  "Replacement Year",
  "Expected Lifetime",
  "Simplified Domicile",
  "EV Purchase Cost pre-incentive",
  "Default Replacement Allocation",
  "EV Annual Maintenance Costs",
  "Existing Vehicle Annual Maintenance",
  "EV Annual Charging Costs",
  "Existing Vehicle Annual Fuel Costs",
  "state_incentives",
  "IRA Incentives",
  "Annual KWh",
  "ghg",
  "electrification_scenario",
  "vehicle_type",
  "charger_assignment",
  "inflation_start_year",
];

// Important: this list needs to exactly match the columns in the fleet data table
export const phasesDataList = [
  "id",
  "user_id",
  "site",
  "year",
  "ports",
  "loan_amount",
  "port_10_20_kw",
  "port_25_kw",
  "port_180_200_kw",
  "port_less_than_10_kw",
  "trenching_costs",
  "upgrade_cost_utility",
  "upgrade_cost_customer",
  "procurement_management_cost",
  "incentives",
  "capital_planning_funding",
  "estimated_public_works_engineering_costs",
  "master_access_incentives",
  "other_costs",
  "other_costs_description",
];

export const saveDefault = async (userId, tableName) => {
  const supabase = getAuthenticatedClient();

  const { data, error } = await supabase
    .from(tableName)
    .select("*")
    .eq(tableName == "advanced controls" ? "id" : "user_id", userId);

  if (error) {
    return { error: "Failed to fetch data from Supabase" };
  }

  const mapping = {
    "advanced controls": "advanced_controls",
    "fleet data": "fleet_data",
    phases: "infrastructure_phases",
  };
  const field = mapping[tableName];

  if (tableName === "advanced controls") {
    const { data: controlsData, error } = await supabase
      .from("controls")
      .select("*")
      .eq("id", userId);

    const { data: updateData, error: updateError } = await supabase
      .from("default data")
      .update({ advanced_controls: data, controls: controlsData })
      .eq("id", userId);

    if (updateError) {
      return { error: "Failed to update default data in Supabase" };
    }
  } else {
    const { data: updateData, error: updateError } = await supabase
      .from("default data")
      .update({ [field]: data })
      .eq("id", userId);

    if (updateError) {
      return { error: "Failed to update default data in Supabase" };
    }
  }

  return { message: "Default data saved and updated successfully" };
};

export const updateDefault = async (userId, tableName) => {
  const supabase = getAuthenticatedClient();

  try {
    const mapping = {
      "advanced controls": "advanced_controls",
      controls: "controls",
      "fleet data": "fleet_data",
      phases: "infrastructure_phases",
    };

    const { data: defaultData } = await supabase
      .from("default data")
      .select("*")
      .eq("id", userId);

    // Handle "charger costs" table reset
    if (tableName === "charger costs") {
      if (defaultData.length === 0 || !defaultData[0].charger_costs) {
        return { error: "Default charger costs data not found" };
      }

      // Extract charger_costs from the JSONB column
      const chargerCostsData = Array.isArray(defaultData[0].charger_costs)
        ? defaultData[0].charger_costs[0]
        : defaultData[0].charger_costs;

      // Copy data from "default data" table's charger_costs column to "charger costs" table
      const { error } = await supabase
        .from("charger costs")
        .upsert([{ id: userId, ...chargerCostsData }])
        .select();

      if (error) {
        throw new Error(`Error updating charger costs: ${error.message}`);
      }

      return { message: "Default charger costs data restored" };
    }

    let jsonObject, advancedControlsJsonObject, updatedJsonObject;

    if (tableName === "advanced controls") {
      if (
        defaultData.length === 0 ||
        defaultData[0].advanced_controls === null ||
        defaultData[0].controls === null
      ) {
        advancedControlsJsonObject = {
          advanced_controls: [
            {
              id: userId,
              inflation: false,
              incentive_cap: null,
              discount_rate_npv: 3,
              site_incentive_cap: null,
              charger_network_costs: 0,
              charging_optimization: false,
              year_of_analysis_start: 2024,
              gasoline_escalation_rate: 3,
              infrastructure_loan_term: 10,
              inflation_escalation_rate: 3,
              incentive_per_low_power_l2: null,
              electricity_escalation_rate: 3,
              incentive_per_high_power_l2: null,
              infrastructure_loan_enabled: false,
              charging_optimization_savings: 1000,
              charger_install_costs_adjustment: null,
              charger_purchase_costs_adjustment: null,
              infrastructure_loan_interest_rate: 3,
              charge_management_subscription_costs: 0,
              maintenance_costs_annual_per_station: 400,
            },
          ],
          controls: [
            {
              id: userId,
              site: "",
              incentives: true,
              ira_incentives: true,
              evse_incentives: true,
              electrification_scenario: "",
            },
          ],
        };
      } else {
        advancedControlsJsonObject = {
          advanced_controls: defaultData[0].advanced_controls,
          controls: defaultData[0].controls,
        };
      }

      // update supabase database
      const { data: deletedAdvancedControls, error: currentAdvancedControls } =
        await supabase
          .from("advanced controls")
          .delete()
          .eq("id", userId)
          .select();

      const { data: deletedControls, error: currentControls } = await supabase
        .from("controls")
        .delete()
        .eq("id", userId)
        .select();

      if (currentAdvancedControls || currentControls) {
        throw new Error(
          `Error delete current data: ${currentAdvancedControls?.message} or ${currentControls?.message}`,
        );
      }

      const { error: advancedControlsError } = await supabase
        .from("advanced controls")
        .insert(advancedControlsJsonObject.advanced_controls)
        .select();

      if (advancedControlsError) {
        if (deletedAdvancedControls?.length) {
          await supabase
            .from("advanced controls")
            .insert(deletedAdvancedControls);
        }
        throw new Error(
          `Error inserting data: ${advancedControlsError.message}`,
        );
      }

      const { error: controlsError } = await supabase
        .from("controls")
        .insert(advancedControlsJsonObject.controls)
        .select();

      if (controlsError) {
        if (deletedControls?.length) {
          await supabase.from("controls").insert(deletedControls);
        }
        if (deletedAdvancedControls?.length) {
          await supabase
            .from("advanced controls")
            .insert(deletedAdvancedControls);
        }
        throw new Error(`Error inserting data: ${controlsError.message}`);
      }
    } else {
      jsonObject = defaultData[0][mapping[tableName]];

      if (tableName === "phases") {
        if (jsonObject === null) {
          const { data: phaseData } = await supabase
            .from("phases")
            .select("*")
            .eq("user_id", userId)
            .select();

          const { data: updateData, error: updateError } = await supabase
            .from("default data")
            .update({ infrastructure_phases: phaseData })
            .eq("id", userId)
            .select();

          if (updateError) {
            throw new Error(`Error inserting data: ${updateError.message}`);
          }
          return { message: "Add default data" };
        } else {
          updatedJsonObject = jsonObject;
        }
      } else if (tableName === "fleet data") {
        if (jsonObject === null) {
          return { error: `Default ${tableName} not found` };
        } else {
          updatedJsonObject = jsonObject.map((item) => {
            const newItem = {};
            fleetDataList.forEach((key) => {
              newItem[key] = key in item ? item[key] : null;
            });

            return newItem;
          });
        }
      }

      const delRes = await supabase
        .from(tableName)
        .delete()
        .eq(tableName == "advanced controls" ? "id" : "user_id", userId)
        .select();

      const { error } = await supabase
        .from(tableName)
        .insert(updatedJsonObject)
        .select();

      if (error) {
        await supabase.from(tableName).insert(delRes.data);
        throw new Error(`Error inserting data: ${error.message}`);
      }
    }

    return { message: "Default data restored" };
  } catch (error) {
    return { error: error.message };
  }
};
